import { c as _c } from "react/compiler-runtime";
import { isEmail, isPhoneNumber } from '@onefootprint/core';
import { PhoneInput, TextInput } from '@onefootprint/ui';
import { forwardRef, useImperativeHandle } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
export type UserSpecificFormData = {
  email: string;
  phone: string;
  external_id: string | undefined;
};
export type UserSpecificFormHandle = {
  setExternalIdError: (message: string) => void;
};
export type UserSpecificFormProps = {
  onSubmit: (data: UserSpecificFormData) => void;
  defaultValues?: UserSpecificFormData;
  requiresAuthentication?: boolean;
};
const UserSpecificForm = forwardRef<UserSpecificFormHandle, UserSpecificFormProps>((t0, ref) => {
  const $ = _c(48);
  const {
    onSubmit,
    defaultValues: t1,
    requiresAuthentication: t2
  } = t0;
  let t3;
  if ($[0] !== t1) {
    t3 = t1 === undefined ? {
      email: "",
      phone: "",
      external_id: undefined
    } : t1;
    $[0] = t1;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const defaultValues = t3;
  const requiresAuthentication = t2 === undefined ? true : t2;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      keyPrefix: "table.actions.copyLinkDialog.config.attributes"
    };
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const {
    t
  } = useTranslation("playbooks", t4);
  let t5;
  if ($[3] !== defaultValues) {
    t5 = {
      defaultValues,
      mode: "onChange"
    };
    $[3] = defaultValues;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const form = useForm(t5);
  let t6;
  let t7;
  if ($[5] !== form) {
    t6 = () => ({
      setExternalIdError: message => {
        form.setError("external_id", {
          type: "server",
          message
        }, {
          shouldFocus: true
        });
      }
    });
    t7 = [form];
    $[5] = form;
    $[6] = t6;
    $[7] = t7;
  } else {
    t6 = $[6];
    t7 = $[7];
  }
  useImperativeHandle(ref, t6, t7);
  let t8;
  if ($[8] !== form || $[9] !== onSubmit) {
    t8 = form.handleSubmit(onSubmit);
    $[8] = form;
    $[9] = onSubmit;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const handleSubmit = t8;
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = ["email", "phone"];
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== form.control) {
    t10 = {
      control: form.control,
      name: t9
    };
    $[12] = form.control;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const [email, phone] = useWatch(t10);
  let t11;
  let t12;
  if ($[14] !== t) {
    t11 = {
      validate: value => {
        if (!value) {
          return true;
        }
        return isPhoneNumber(value) || t(_temp);
      }
    };
    t12 = t13 => {
      const {
        field: t14,
        fieldState: t15
      } = t13;
      const {
        onChange,
        onBlur,
        value: value_0,
        name
      } = t14;
      const {
        error
      } = t15;
      return <PhoneInput size="compact" name={name} label={t(_temp2)} onBlur={onBlur} value={value_0} popper={true} onChange={onChange} hasError={!!error} hint={error?.message} />;
    };
    $[14] = t;
    $[15] = t11;
    $[16] = t12;
  } else {
    t11 = $[15];
    t12 = $[16];
  }
  let t13;
  if ($[17] !== form.control || $[18] !== t11 || $[19] !== t12) {
    t13 = <Controller control={form.control} name="phone" rules={t11} render={t12} />;
    $[17] = form.control;
    $[18] = t11;
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  let t15;
  if ($[21] !== t) {
    t14 = {
      validate: value_1 => {
        if (!value_1) {
          return true;
        }
        return isEmail(value_1) || t(_temp3);
      }
    };
    t15 = t16 => {
      const {
        field: t17,
        fieldState: t18
      } = t16;
      const {
        onChange: onChange_0,
        onBlur: onBlur_0,
        value: value_2,
        name: name_0
      } = t17;
      const {
        error: error_0
      } = t18;
      return <TextInput size="compact" name={name_0} onBlur={onBlur_0} value={value_2} onChange={onChange_0} label={t(_temp4)} placeholder={t(_temp5)} type="email" hasError={!!error_0} hint={error_0?.message} />;
    };
    $[21] = t;
    $[22] = t14;
    $[23] = t15;
  } else {
    t14 = $[22];
    t15 = $[23];
  }
  let t16;
  if ($[24] !== form.control || $[25] !== t14 || $[26] !== t15) {
    t16 = <Controller control={form.control} name="email" rules={t14} render={t15} />;
    $[24] = form.control;
    $[25] = t14;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== email || $[29] !== phone || $[30] !== requiresAuthentication || $[31] !== t) {
    t17 = {
      validate: value_3 => {
        if (!requiresAuthentication && (email || phone) && !value_3) {
          return t(_temp6);
        }
        return true;
      }
    };
    $[28] = email;
    $[29] = phone;
    $[30] = requiresAuthentication;
    $[31] = t;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== form || $[34] !== t) {
    t18 = t19 => {
      const {
        field: t20,
        fieldState: t21
      } = t19;
      const {
        onChange: onChange_1,
        onBlur: onBlur_1,
        value: value_4,
        name: name_1
      } = t20;
      const {
        error: error_1
      } = t21;
      return <TextInput size="compact" name={name_1} onBlur={onBlur_1} value={value_4 || ""} onChange={e => {
        if (error_1?.type === "server") {
          form.clearErrors("external_id");
        }
        onChange_1(e);
      }} label={t(_temp7)} placeholder={t(_temp8)} hasError={!!error_1} hint={error_1?.message} />;
    };
    $[33] = form;
    $[34] = t;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== form.control || $[37] !== t17 || $[38] !== t18) {
    t19 = <Controller control={form.control} name="external_id" rules={t17} render={t18} />;
    $[36] = form.control;
    $[37] = t17;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== handleSubmit || $[41] !== t13 || $[42] !== t16 || $[43] !== t19) {
    t20 = <form id="user-specific-form" onSubmit={handleSubmit} className="flex flex-col gap-4">{t13}{t16}{t19}</form>;
    $[40] = handleSubmit;
    $[41] = t13;
    $[42] = t16;
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== form || $[46] !== t20) {
    t21 = <FormProvider {...form}>{t20}</FormProvider>;
    $[45] = form;
    $[46] = t20;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  return t21;
});
UserSpecificForm.displayName = 'UserSpecificForm';
export default UserSpecificForm;
function _temp($) {
  return $.phone.errors.invalid;
}
function _temp2($_0) {
  return $_0.phone.label;
}
function _temp3($_1) {
  return $_1.email.errors.invalid;
}
function _temp4($_2) {
  return $_2.email.label;
}
function _temp5($_3) {
  return $_3.email.placeholder;
}
function _temp6($_4) {
  return $_4.external_id.errors.required;
}
function _temp7($_5) {
  return $_5.external_id.label;
}
function _temp8($_6) {
  return $_6.external_id.placeholder;
}